import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';

const SearchableSelect = ({
    options = [],
    value,
    onChange,
    placeholder = "Select an option",
    disabled = false,
    error = false,
    required = false,
    label,
    id,
    name,
    className = "",
    searchPlaceholder,
    noOptionsText,
    maxHeight = "15rem",
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    onFocus,
    onBlur
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [focusedIndex, setFocusedIndex] = useState(-1);

    const dropdownRef = useRef(null);
    const inputRef = useRef(null);
    const triggerRef = useRef(null);
    const optionRefs = useRef([]);

    // Generate unique IDs for accessibility
    const selectId = useMemo(() =>
        id || `searchable-select-${Math.random().toString(36).substr(2, 9)}`,
        [id]
    );
    const listboxId = `${selectId}-listbox`;
    const searchId = `${selectId}-search`;

    // Memoize filtered options for performance
    const filteredOptions = useMemo(() =>
        options.filter(option =>
            option.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (option.code && option.code.toLowerCase().includes(searchTerm.toLowerCase()))
        ),
        [options, searchTerm]
    );

    // Get the selected option's display name
    const selectedOption = useMemo(() =>
        options.find(option => option.name === value || option.code === value),
        [options, value]
    );

    const displayValue = selectedOption?.name || value || placeholder;

    // Dynamic placeholders based on context
    const dynamicSearchPlaceholder = searchPlaceholder ||
        (placeholder.toLowerCase().includes('bank') ? 'Search banks...' : 'Search options...');

    const dynamicNoOptionsText = noOptionsText ||
        (placeholder.toLowerCase().includes('bank') ? 'No banks found' : 'No options found');

    // Cleanup function for event listeners
    const cleanup = useCallback(() => {
        setIsOpen(false);
        setSearchTerm('');
        setFocusedIndex(-1);
    }, []);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                cleanup();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isOpen, cleanup]);

    // Handle escape key and other global keyboard events
    useEffect(() => {
        const handleGlobalKeyDown = (event) => {
            if (event.key === 'Escape' && isOpen) {
                cleanup();
                triggerRef.current?.focus();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleGlobalKeyDown);
        }

        return () => {
            document.removeEventListener('keydown', handleGlobalKeyDown);
        };
    }, [isOpen, cleanup]);

    // Reset focused index when filtered options change
    useEffect(() => {
        if (focusedIndex >= filteredOptions.length) {
            setFocusedIndex(filteredOptions.length > 0 ? 0 : -1);
        }
    }, [filteredOptions.length, focusedIndex]);

    // Scroll focused option into view
    useEffect(() => {
        if (focusedIndex >= 0 && optionRefs.current[focusedIndex]) {
            optionRefs.current[focusedIndex].scrollIntoView({
                block: 'nearest',
                behavior: 'smooth'
            });
        }
    }, [focusedIndex]);

    const openDropdown = useCallback(() => {
        if (disabled) return;
        setIsOpen(true);
        onFocus?.();
        // Focus search input after dropdown opens
        setTimeout(() => inputRef.current?.focus(), 50);
    }, [disabled, onFocus]);

    const closeDropdown = useCallback(() => {
        cleanup();
        onBlur?.();
    }, [cleanup, onBlur]);

    const handleSelect = useCallback((option) => {
        if (onChange) {
            onChange(option);
        }
        closeDropdown();
        triggerRef.current?.focus();
    }, [onChange, closeDropdown]);

    const toggleDropdown = useCallback(() => {
        if (isOpen) {
            closeDropdown();
        } else {
            openDropdown();
        }
    }, [isOpen, closeDropdown, openDropdown]);

    // Handle keyboard navigation on trigger
    const handleTriggerKeyDown = (event) => {
        switch (event.key) {
            case 'Enter':
            case ' ':
                event.preventDefault();
                if (!isOpen) {
                    openDropdown();
                } else if (focusedIndex >= 0 && filteredOptions[focusedIndex]) {
                    handleSelect(filteredOptions[focusedIndex]);
                }
                break;
            case 'ArrowDown':
                event.preventDefault();
                if (!isOpen) {
                    openDropdown();
                } else {
                    setFocusedIndex(prev =>
                        prev < filteredOptions.length - 1 ? prev + 1 : 0
                    );
                }
                break;
            case 'ArrowUp':
                event.preventDefault();
                if (isOpen) {
                    setFocusedIndex(prev =>
                        prev > 0 ? prev - 1 : filteredOptions.length - 1
                    );
                }
                break;
            case 'Home':
                if (isOpen) {
                    event.preventDefault();
                    setFocusedIndex(0);
                }
                break;
            case 'End':
                if (isOpen) {
                    event.preventDefault();
                    setFocusedIndex(filteredOptions.length - 1);
                }
                break;
        }
    };

    // Handle keyboard navigation in search input
    const handleSearchKeyDown = (event) => {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                setFocusedIndex(filteredOptions.length > 0 ? 0 : -1);
                break;
            case 'ArrowUp':
                event.preventDefault();
                setFocusedIndex(filteredOptions.length > 0 ? filteredOptions.length - 1 : -1);
                break;
            case 'Enter':
                event.preventDefault();
                if (focusedIndex >= 0 && filteredOptions[focusedIndex]) {
                    handleSelect(filteredOptions[focusedIndex]);
                } else if (filteredOptions.length === 1) {
                    handleSelect(filteredOptions[0]);
                }
                break;
            case 'Tab':
                if (event.shiftKey) {
                    closeDropdown();
                }
                break;
        }
    };

    const handleSearchChange = useCallback((e) => {
        setSearchTerm(e.target.value);
        setFocusedIndex(-1);
    }, []);

    // Dynamic CSS classes
    const triggerClasses = `
        relative w-full px-3 py-2 text-left bg-white border rounded-lg 
        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
        transition-colors duration-200
        ${disabled
            ? 'bg-gray-50 text-gray-500 cursor-not-allowed'
            : 'cursor-pointer hover:border-gray-400'
        }
        ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'}
        ${className}
    `.trim().replace(/\s+/g, ' ');

    return (
        <div className="relative" ref={dropdownRef}>
            {label && (
                <label
                    htmlFor={selectId}
                    className={`block text-sm font-medium mb-1 ${error ? 'text-red-700' : 'text-gray-700'
                        }`}
                >
                    {label}
                    {required && <span className="text-red-500 ml-1" aria-label="required">*</span>}
                </label>
            )}

            {/* Hidden input for form submission */}
            {name && (
                <input
                    type="hidden"
                    name={name}
                    value={selectedOption?.code || selectedOption?.name || value || ''}
                />
            )}

            {/* Trigger Button */}
            <button
                ref={triggerRef}
                id={selectId}
                type="button"
                className={triggerClasses}
                onClick={toggleDropdown}
                onKeyDown={handleTriggerKeyDown}
                disabled={disabled}
                role="combobox"
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                aria-controls={isOpen ? listboxId : undefined}
                aria-label={ariaLabel || label || `Select ${placeholder.toLowerCase()}`}
                aria-describedby={ariaDescribedBy}
                aria-required={required}
                aria-invalid={error}
            >
                <div className="flex items-center justify-between w-full">
                    <span className={`block truncate text-left ${!selectedOption ? 'text-gray-500' : 'text-gray-900'
                        }`}>
                        {displayValue}
                    </span>
                    <span
                        className={`ml-2 transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''
                            }`}
                        aria-hidden="true"
                    >
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </span>
                </div>
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                    {/* Search Input */}
                    <div className="sticky top-0 bg-white p-3 border-b border-gray-200 rounded-t-lg">
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                            <input
                                ref={inputRef}
                                id={searchId}
                                type="text"
                                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder={dynamicSearchPlaceholder}
                                value={searchTerm}
                                onChange={handleSearchChange}
                                onKeyDown={handleSearchKeyDown}
                                onClick={(e) => e.stopPropagation()}
                                aria-label="Search options"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </div>
                    </div>



                    {/* Options List */}
                    <div
                        className="py-1 overflow-y-auto"
                        style={{ maxHeight }}
                        role="listbox"
                        id={listboxId}
                        aria-label="Available options"
                    >
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option, index) => {
                                const isSelected = selectedOption?.code === option.code ||
                                    selectedOption?.name === option.name;
                                const isFocused = index === focusedIndex;

                                // Generate a unique key by combining multiple identifiers
                                const uniqueKey = `${option.code || 'no-code'}-${option.name || 'no-name'}-${index}`;

                                return (
                                    <div
                                        key={uniqueKey}
                                        ref={el => optionRefs.current[index] = el}
                                        className={`
                        px-3 py-2 cursor-pointer flex items-center justify-between
                        transition-colors duration-150
                        ${isFocused ? 'bg-blue-50 text-blue-900' : 'hover:bg-gray-50'}
                        ${isSelected ? 'bg-blue-100 text-blue-900 font-medium' : 'text-gray-900'}
                    `.trim().replace(/\s+/g, ' ')}
                                        onClick={() => handleSelect(option)}
                                        role="option"
                                        aria-selected={isSelected}
                                        aria-label={option.name}
                                    >
                                        <span className="block truncate">{option.name}</span>
                                        {isSelected && (
                                            <svg className="w-4 h-4 text-blue-600 ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </div>
                                );
                            })
                        ) : (
                            <div className="px-3 py-2 text-gray-500 text-center" role="option" aria-disabled="true">
                                {dynamicNoOptionsText}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchableSelect;